import React from "react";
import { formatPunchListBandValue } from "./expertBandPresentation";
import FixedScaleRangeBand, { boundedScaleValue, scalePercent } from "./FixedScaleRangeBand";


export default function ExpertFeedbackBand({
  parameter,
  presentation,
  coreVisible = true,
  priorLearnerValue,
  learnerConfidence,
  panelRaterMeans,
  panelRaterRequiredCount,
}) {
  const { raw, display } = presentation;
  const prior = Number.isFinite(Number(priorLearnerValue)) ? Number(priorLearnerValue) : null;
  const learner = boundedScaleValue(raw.learnerValue);
  const confidence = Number.isFinite(Number(learnerConfidence)) ? Number(learnerConfidence) : null;
  const accessibleDescription = `${parameter}. Your rating ${formatPunchListBandValue(raw.learnerValue)}.${confidence === null ? "" : ` Confidence ${formatPunchListBandValue(confidence)}.`} Observed expert range ${formatPunchListBandValue(raw.observedLow)} to ${formatPunchListBandValue(raw.observedHigh)}. Agreement window ${formatPunchListBandValue(raw.outerLow)} to ${formatPunchListBandValue(raw.outerHigh)}. ${raw.n} reference ratings.`;
  const joinedLow = prior === null ? null : Math.min(prior, learner);
  const joinedHigh = prior === null ? null : Math.max(prior, learner);
  const learnerLabel = `You · ${formatPunchListBandValue(raw.learnerValue)}`;
  const requiredPanelRaterCount = Number(panelRaterRequiredCount);
  const governedPanelMeans = Number.isInteger(requiredPanelRaterCount) && requiredPanelRaterCount > 0 &&
    Array.isArray(panelRaterMeans) && panelRaterMeans.length === requiredPanelRaterCount &&
    panelRaterMeans.every(value => Number.isFinite(Number(value)))
    ? panelRaterMeans.map(Number)
    : [];
  const hasGovernedPanelMeans = requiredPanelRaterCount > 0 &&
    governedPanelMeans.length === requiredPanelRaterCount;
  const panelEvidence = hasGovernedPanelMeans
    ? ` Rater averages, two listens each: ${governedPanelMeans.map(formatPunchListBandValue).join(", ")}.`
    : "";
  const completeAccessibleDescription = `${accessibleDescription}${panelEvidence}`;
  const rangeEvidence = <p className="av-governed-feedback-band__range-evidence">
    Observed panel range: {formatPunchListBandValue(raw.observedLow)}–{formatPunchListBandValue(raw.observedHigh)}. Agreement window: {formatPunchListBandValue(raw.outerLow)}–{formatPunchListBandValue(raw.outerHigh)}.
  </p>;
  const footer = <>
        {rangeEvidence}
        {hasGovernedPanelMeans && <p className="av-governed-feedback-band__panel-evidence">
          Rater averages, two listens each: {governedPanelMeans.map(formatPunchListBandValue).join(", ")}
        </p>}
      </>;

  return <>
    {confidence !== null && <p className="av-governed-feedback-band__response" data-testid="expert-band-response">
      <strong>Your response:</strong> Rating {formatPunchListBandValue(raw.learnerValue)} / 100 · Confidence {formatPunchListBandValue(confidence)} / 100
    </p>}
    <FixedScaleRangeBand
      ariaLabel={completeAccessibleDescription}
      data-testid="governed-feedback-band"
      data-reference-count={raw.n}
      learnerLabel={learnerLabel}
      learnerValue={learner}
      footer={footer}
      primaryRange={{ low: display.outerLow, high: display.outerHigh,
        className: "av-governed-feedback-band__outer", testId: "expert-band-outer" }}
      secondaryRange={coreVisible ? { low: display.coreLow, high: display.coreHigh,
        className: "av-governed-feedback-band__core", testId: "expert-band-core" } : null}
      rangeLabels={{
        lowLabel: formatPunchListBandValue(raw.observedLow),
        highLabel: formatPunchListBandValue(raw.observedHigh),
        lowPosition: raw.observedLow,
        highPosition: raw.observedHigh,
        lowTestId: "expert-band-range-low",
        highTestId: "expert-band-range-high",
      }}
    >
      <span className="av-governed-feedback-band__mean" data-testid="expert-band-mean" style={{ "--tick-left": scalePercent(raw.mean) }} />
      {governedPanelMeans.map((value, index) => <span
        aria-hidden="true"
        className="av-governed-feedback-band__panel-rater"
        data-testid="expert-band-panel-rater"
        key={`${index}:${value}`}
        style={{ "--tick-left": scalePercent(value) }}
      />)}
      {prior !== null && <>
        <span className="av-governed-feedback-band__joined" data-testid="expert-band-learner-join" style={{ "--band-left": scalePercent(joinedLow), "--band-width": `${Math.max(0, boundedScaleValue(joinedHigh) - boundedScaleValue(joinedLow))}%` }} />
        <span className="av-governed-feedback-band__learner av-governed-feedback-band__learner--prior" data-testid="expert-band-prior-learner" style={{ "--tick-left": scalePercent(prior) }} />
      </>}
    </FixedScaleRangeBand>
  </>;
}
